import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { pushSupported, getSubscription, subscribePush, unsubscribePush } from "../lib/push";

export default function PushToggle() {
  const supported = pushSupported();
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supported) { setLoading(false); return; }
    getSubscription()
      .then((s) => setSubscribed(!!s))
      .finally(() => setLoading(false));
  }, [supported]);

  const toggle = async () => {
    setBusy(true);
    try {
      if (subscribed) {
        await unsubscribePush();
        setSubscribed(false);
        toast.success("Notifications turned off");
      } else {
        await subscribePush(["engagement", "social"]);
        setSubscribed(true);
        toast.success("You're in! We'll ping you for streak warnings, new perks & sponsored challenges.");
      }
    } catch (e) {
      toast.error(e.message || "Couldn't update notifications");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass-card p-7 mt-5" data-testid="push-toggle-card">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${subscribed ? "bg-neongreen/15 text-neongreen" : "bg-neonpink/15 text-neonpink"}`}>
          {subscribed ? <Bell size={22}/> : <BellOff size={22}/>}
        </div>
        <div className="flex-1">
          <h2 className="font-display text-xl font-extrabold">Push notifications</h2>
          <p className="text-sm text-white/60 mt-1">
            Streak-break warnings, new perks from our partners, weekly wrap-ups & sponsored challenges.
            We won't spam you.
          </p>
          {!supported && (
            <p className="text-xs text-neonyellow mt-3" data-testid="push-unsupported">
              Your browser doesn't support push. Try Chrome, Edge, Firefox or Safari (iOS 16.4+).
            </p>
          )}
          {supported && (
            <button
              onClick={toggle}
              disabled={busy || loading}
              data-testid="push-toggle-button"
              className={`mt-4 ${subscribed ? "btn-secondary-neon" : "btn-primary-neon"} text-sm flex items-center gap-2`}
            >
              {(busy || loading) && <Loader2 size={14} className="animate-spin"/>}
              {loading ? "Checking…" : subscribed ? "Turn off notifications" : "Turn on notifications"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
